import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, fmtDate } from "@/lib/format";
import { StatCard, Empty, Cell } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import QnbIncomingDialog, { incomingStatus, profileLabel } from "@/components/QnbIncomingDialog";
import HtmlPreviewDialog, { fetchPreviewUrl, buildIncomingHtml } from "@/components/HtmlPreviewDialog";
import ReturnInvoiceDialog from "@/components/ReturnInvoiceDialog";
import QnbInvoiceDialog from "@/components/QnbInvoiceDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { fileUrl } from "@/lib/api";
import { Inbox, RefreshCw, FlaskConical, Search, CheckCircle2, XCircle, Loader2, Receipt, AlertTriangle, Eye, FileText, MoreHorizontal, Undo2, FileCode2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const FILTERS = [
  { key: "all", label: "Tümü" }, { key: "yeni", label: "Yeni" }, { key: "kabul", label: "Kabul" }, { key: "red", label: "Red" },
];
const fmtDT = (s) => (s ? `${fmtDate(s)}${s.length > 10 ? " " + s.slice(11, 16) : ""}` : "—");

// QNB'den gelen e-Fatura / e-İrsaliye listesi (Alış Faturaları + e-Fatura sayfalarında ortak)
export default function QnbIncomingPanel({ defaultType = "FATURA", showTypeToggle = true }) {
  const [docType, setDocType] = useState(defaultType);
  const [rows, setRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [sel, setSel] = useState(null);
  const [simInfo, setSimInfo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(null);
  const [returnDoc, setReturnDoc] = useState(null);
  const [returnBusy, setReturnBusy] = useState(null);
  const [returnInv, setReturnInv] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [checked, setChecked] = useState([]);        // toplu KABUL için seçili gelen e-Fatura id'leri
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStock, setBulkStock] = useState(false);

  const load = () => {
    api.get(`/qnb/incoming?doc_type=${docType}`).then((r) => setRows((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data))).catch(() => {});
  };
  useAutoRefresh(load, { interval: 30000 });
  useEffect(() => { api.get("/qnb/status").then((r) => setCfg(r.data)).catch(() => {}); api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  // GİB belge görüntüsü (QNB PDF/HTML; TEST simülasyonunda yerel şablon)
  const openPreview = async (r) => {
    setPreviewBusy(r.id);
    try {
      if (r.simulated) { const { data } = await api.get(`/qnb/incoming/${r.id}`); setPreview({ html: buildIncomingHtml(data), title: r.belge_no }); }
      else setPreview({ url: await fetchPreviewUrl(`/qnb/incoming/${r.id}/document`), title: r.belge_no });
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewBusy(null); }
  };
  const openUbl = async (r) => {
    try { const { data } = await api.get(`/qnb/incoming/${r.id}/document?fmt=UBL`); window.open(fileUrl(data.url), "_blank"); }
    catch (e) { toast.error(apiError(e)); }
  };
  // Gelen faturadan İADE faturası taslağı (kalem seç → önizle & kes)
  const openReturn = async (r) => {
    setReturnBusy(r.id);
    try {
      const { data } = await api.get(`/qnb/incoming/${r.id}`);
      if (!(data.parsed?.lines || []).length) return toast.error("Bu faturanın kalem bilgisi okunamadı — iade taslağı oluşturulamıyor");
      setReturnDoc(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setReturnBusy(null); }
  };

  const list = useMemo(() => {
    let r = rows;
    if (filter !== "all") r = r.filter((x) => (x.status || "yeni") === filter);
    if (q.trim()) {
      const s = q.toLocaleLowerCase("tr");
      r = r.filter((x) => (x.sender_name || "").toLocaleLowerCase("tr").includes(s) || (x.belge_no || "").toLowerCase().includes(s) || (x.sender_vkn || "").includes(s));
    }
    return r;
  }, [rows, filter, q]);

  const cnt = (s) => rows.filter((x) => (x.status || "yeni") === s).length;
  const sumOpen = rows.filter((x) => x.status !== "red").reduce((a, x) => a + (x.payable || 0), 0);
  const selectable = list.filter((x) => x.doc_type === "FATURA" && (x.status || "yeni") === "yeni");
  const checkedRows = rows.filter((x) => checked.includes(x.id));
  const checkedSum = checkedRows.reduce((a, x) => a + (x.payable || 0), 0);
  const toggle = (id) => setChecked((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setChecked((s) => (selectable.every((x) => s.includes(x.id)) ? s.filter((id) => !selectable.some((x) => x.id === id)) : Array.from(new Set([...s, ...selectable.map((x) => x.id)]))));
  useEffect(() => { setChecked([]); }, [docType]);

  // Toplu KABUL — tek onay, sırayla yanıt (her fatura ayrı GİB yanıtı + alış faturası + cari borç)
  const bulkAccept = async () => {
    if (!checked.length) return;
    const ticari = checkedRows.filter((x) => (x.profile || "").toUpperCase() === "TICARIFATURA").length;
    const ok = await confirm(`${checked.length} gelen e-Fatura KABUL edilecek (toplam ${TRY(checkedSum)}).${ticari ? ` ${ticari} ticari faturaya GİB'e KABUL yanıtı gönderilir.` : ""} Her biri için alış faturası oluşturulup tedarikçi carisine borç işlenir${bulkStock ? " ve kalemler stoğa eklenir" : ""}. Bu işlem geri alınamaz — devam edilsin mi?`);
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/qnb/incoming/bulk-respond", { ids: checked, response: "KABUL", add_to_stock: bulkStock });
      const fails = (data.results || []).filter((r) => !r.ok);
      if (fails.length) toast.warning(`${data.ok_count} fatura KABUL edildi · ${fails.length} hata: ${fails.slice(0, 3).map((f) => f.error).join(" · ")}`, { duration: 10000 });
      else toast.success(`${data.ok_count} gelen e-Fatura KABUL edildi · alış faturaları oluşturuldu`);
      setChecked([]);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/qnb/incoming/sync");
      const n = (data.incoming_fatura || 0) + (data.incoming_irsaliye || 0);
      (data.errors || []).length ? toast.warning(`Senkron tamamlandı · ${data.errors.join(" · ")}`) : toast.success(n ? `${n} yeni belge alındı` : "Yeni belge yok — liste güncel");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };
  const simulate = async () => {
    if (!await confirm("TEST: Karşı firma (erpefaturatest2) bizim VKN'mize örnek bir e-Fatura gönderecek. Devam edilsin mi?")) return;
    setSimulating(true); setSimInfo(null);
    try {
      const { data } = await api.post("/qnb/simulate/incoming", { profile: "TICARIFATURA" });
      setSimInfo(data);
      if (data.mode === "local") toast.warning("QNB test2 alıcıyı tanımıyor → fatura YEREL simülasyon olarak gelen kutusuna işlendi", { duration: 8000 });
      else toast.success(`Karşı taraftan fatura gönderildi (${data.doc_id}) · 15-60 sn içinde gelen kutusuna düşer`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSimulating(false); }
  };

  const sync_ = cfg?.sync || {};
  const lastSync = docType === "FATURA" ? sync_.last_sync_FATURA : sync_.last_sync_IRSALIYE;

  return (
    <div className="space-y-5" data-testid="qnb-incoming-panel">
      {cfg && !cfg.efatura_ready && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 flex items-center gap-3" data-testid="incoming-config-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />Gelen e-Fatura için QNB e-Fatura connector yapılandırması gerekli (Kullanıcılar → QNB paneli).
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Yeni (Yanıt Bekleyen)" value={cnt("yeni")} icon={Inbox} tone="blue" />
        <StatCard label="Kabul Edilen" value={cnt("kabul")} icon={CheckCircle2} tone="success" />
        <StatCard label="Reddedilen" value={cnt("red")} icon={XCircle} tone="danger" />
        <StatCard label="Toplam Tutar (red hariç)" value={TRY(sumOpen)} icon={Receipt} sub={`${rows.length} belge`} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {showTypeToggle && <div className="flex gap-1 bg-muted/60 rounded-lg p-1">
            {[["FATURA", "e-Fatura"], ["IRSALIYE", "e-İrsaliye"]].map(([k, l]) => (
              <button key={k} onClick={() => setDocType(k)} data-testid={`incoming-type-${k}`} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${docType === k ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>}
          <div className="flex gap-1 bg-muted/60 rounded-lg p-1">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`incoming-filter-${f.key}`} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${filter === f.key ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{f.label}</button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Gönderen / fatura no / VKN" className="pl-9 h-9" data-testid="incoming-search" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cfg?.simulator && (
            <Button variant="outline" onClick={simulate} disabled={simulating} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="incoming-simulate-btn">
              {simulating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}Test: Karşı taraftan fatura gönder
            </Button>
          )}
          <Button onClick={sync} disabled={syncing} data-testid="incoming-sync-btn">{syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}QNB'den Çek</Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="incoming-sync-info">
        Son senkron: <b>{lastSync ? fmtDT(lastSync) : "—"}</b> · Otomatik kontrol her 60 sn (arka plan) + saatlik cron · Son sıra no: {docType === "FATURA" ? sync_.last_sira_FATURA : sync_.last_sira_IRSALIYE}
        {(sync_.last_result?.errors || []).length > 0 && <span className="text-red-600"> · Hata: {sync_.last_result.errors.join(" · ")}</span>}
      </div>

      {simInfo && (
        <div className={`rounded-xl border p-4 text-sm space-y-1 ${simInfo.mode === "local" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="incoming-sim-result">
          <div className="font-bold flex items-center gap-2"><FlaskConical className="h-4 w-4" />Test gönderimi: {simInfo.doc_id} · {simInfo.sender_title} → bize · {TRY(simInfo.totals?.payable)} · QNB durumu: {simInfo.status_label}</div>
          {simInfo.hint && <div className="text-xs">{simInfo.hint}</div>}
          {simInfo.mode === "qnb" && <div className="text-xs">Belge GİB üzerinden iletiliyor; arka plan senkronu 15-60 sn içinde gelen kutusuna işleyecek. "QNB'den Çek" ile hemen kontrol edebilirsiniz.</div>}
        </div>
      )}

      {list.length === 0 ? (
        <Empty title="Gelen belge yok" desc={docType === "FATURA" ? "QNB'den gelen e-Fatura bulunmuyor. Arka plan senkronu yeni belgeleri otomatik alır." : "QNB'den gelen e-İrsaliye bulunmuyor. Arka plan senkronu yeni belgeleri otomatik alır."} />
      ) : (
        <div className="space-y-2.5" data-testid="incoming-list">
          {selectable.length > 0 && (
            <div className={`rounded-xl border px-4 py-2.5 flex flex-wrap items-center gap-3 transition-colors ${checked.length ? "bg-[#0B192C] text-white border-[#0B192C] shadow-lg" : "bg-card"}`} data-testid="incoming-bulk-bar">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <Checkbox checked={selectable.length > 0 && selectable.every((x) => checked.includes(x.id))} onCheckedChange={toggleAll} className={checked.length ? "border-white data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" : ""} data-testid="incoming-select-all" />
                <span className="font-semibold">{checked.length ? `${checked.length} fatura seçili` : "Tümünü seç"}</span>
                <span className={`text-xs ${checked.length ? "text-white/60" : "text-muted-foreground"}`}>· {selectable.length} yanıt bekleyen</span>
              </label>
              {checked.length > 0 && (
                <>
                  <span className="font-head font-black tabular-nums text-[#ffb27a]" data-testid="incoming-bulk-sum">{TRY(checkedSum)}</span>
                  <label className="flex items-center gap-2 text-xs cursor-pointer ml-auto"><Switch checked={bulkStock} onCheckedChange={setBulkStock} data-testid="incoming-bulk-stock" />Kalemleri stoğa da ekle</label>
                  <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-black" onClick={bulkAccept} disabled={bulkBusy} data-testid="incoming-bulk-accept">
                    {bulkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}{bulkBusy ? "Yanıtlar gönderiliyor…" : `Seçilenleri KABUL Et (${checked.length})`}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 text-white/80 hover:text-white hover:bg-white/10" onClick={() => setChecked([])} disabled={bulkBusy} data-testid="incoming-bulk-clear">Temizle</Button>
                </>
              )}
            </div>
          )}
          {list.map((r) => {
            const st = incomingStatus(r.status);
            const isNew = (r.status || "yeni") === "yeni";
            const isFatura = r.doc_type === "FATURA";
            const stripe = isNew ? "bg-sky-500" : r.status === "kabul" ? "bg-emerald-500" : "bg-red-500";
            const isChecked = checked.includes(r.id);
            return (
              <div key={r.id} className={`group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-shadow hover:shadow-md ${isChecked ? "ring-2 ring-emerald-500 border-emerald-500" : ""} ${!r.read ? "bg-primary/[0.03]" : ""}`} data-testid={`incoming-row-${r.id}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripe}`} />
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  {isNew && isFatura && <Checkbox checked={isChecked} onCheckedChange={() => toggle(r.id)} className="shrink-0 h-5 w-5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`incoming-check-${r.id}`} />}
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
                    <Cell label="Gönderen" className="col-span-2 md:col-span-1">
                      <div className="font-head font-black text-[15px] leading-tight truncate flex items-center gap-1.5" title={r.sender_name}>{r.sender_name}{!r.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" title="Okunmadı" />}</div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">{r.sender_vkn}</div>
                    </Cell>
                    <Cell label="Belge No">
                      <div className="font-mono text-sm font-bold tabular-nums truncate">{r.belge_no}</div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        <Badge variant="outline" className="text-[10px] border-indigo-300 bg-indigo-50 text-indigo-700">{isFatura ? "Gelen e-Fatura" : "Gelen e-İrsaliye"}</Badge>
                        <Badge variant="outline" className="text-[10px]">{profileLabel(r.profile)}</Badge>
                        {r.simulated && <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-700">TEST</Badge>}
                      </div>
                    </Cell>
                    <Cell label="Tarih · Geliş">
                      <div className="text-sm tabular-nums font-semibold">{fmtDate(r.belge_tarihi)}</div>
                      <div className="text-[11px] text-muted-foreground">Geliş {fmtDT(r.gelis_tarihi)}</div>
                    </Cell>
                    <Cell label={isFatura ? "Tutar" : "Kalem"}>
                      {isFatura ? <div className="font-head font-black text-[15px] tabular-nums">{TRY(r.payable)}</div> : <div className="text-sm font-semibold">{(r.parsed?.lines || []).length || "—"} kalem</div>}
                      <div className="text-[11px] text-muted-foreground">{r.invoice_id ? <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><Receipt className="h-3 w-3" />Alış faturası işlendi</span> : isFatura ? "Alış faturası oluşmadı" : "Sevk belgesi"}</div>
                    </Cell>
                    <Cell label="Durum">
                      <Badge variant="outline" className={`${st.cls} font-bold`} data-testid={`incoming-badge-${r.id}`}>{st.label}</Badge>
                    </Cell>
                  </div>
                  <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                    {!isNew && (
                      <span className={`inline-flex items-center h-8 px-2.5 rounded-md text-xs font-bold select-none ${r.status === "kabul" ? "bg-emerald-600 text-white" : "bg-red-100 text-red-700"}`} data-testid={`incoming-answered-${r.id}`}>
                        {r.status === "kabul" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}{r.status === "kabul" ? "KABUL ✓" : "RED"}
                      </span>
                    )}
                    <Button size="sm" variant={isNew && isFatura ? "default" : "outline"} className="h-8" onClick={() => setSel(r)} data-testid={`incoming-open-${r.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" />{isNew && isFatura ? "İncele & Yanıtla" : "Takip"}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openPreview(r)} disabled={previewBusy === r.id} title="GİB belge görüntüsünü önizle" data-testid={`preview-incoming-${r.id}`}>{previewBusy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}</Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`incoming-more-${r.id}`} title="Diğer işlemler">{returnBusy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}</Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        {isFatura && <><DropdownMenuItem onClick={() => openReturn(r)} disabled={returnBusy === r.id} className="text-orange-700 focus:text-orange-800 focus:bg-orange-50 font-semibold" data-testid={`incoming-return-${r.id}`}><Undo2 className="h-4 w-4 mr-2" />İade Faturası Oluştur</DropdownMenuItem><DropdownMenuSeparator /></>}
                        <DropdownMenuItem onClick={() => openPreview(r)} data-testid={`incoming-gib-view-${r.id}`}><Eye className="h-4 w-4 mr-2" />GİB belge görüntüsü</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openUbl(r)} disabled={r.simulated} data-testid={`incoming-ubl-${r.id}`}><FileCode2 className="h-4 w-4 mr-2" />UBL (XML) indir</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <QnbIncomingDialog doc={sel} onClose={() => setSel(null)} onDone={load} />
      <HtmlPreviewDialog open={!!preview} title={preview?.title} url={preview?.url} html={preview?.html} onClose={() => setPreview(null)} />
      <ReturnInvoiceDialog doc={returnDoc} onClose={() => setReturnDoc(null)} onCreated={(inv) => { setReturnDoc(null); load(); setReturnInv(inv); }} />
      <QnbInvoiceDialog invoice={returnInv} contacts={contacts} onClose={() => setReturnInv(null)} onDone={load} />
    </div>
  );
}
